import { BuilderComponent, builder, useIsPreviewing } from '@builder.io/react'
import { BuilderContent } from '@builder.io/sdk'
import { PageOptions } from '@graphcommerce/framer-next-pages'
import { StoreConfigDocument } from '@graphcommerce/magento-store'
import { GetStaticProps } from '@graphcommerce/next-ui'
import DefaultErrorPage from 'next/error'
import { useRouter } from 'next/router'
import {
  BlogList,
  BlogListDocument,
  BlogListQuery,
  BlogPostPathsDocument,
  LayoutDocument,
  LayoutNavigation,
  LayoutNavigationProps,
} from '../../components'
import { PageMeta } from '../../components/Store/PageMeta/PageMeta'
import { graphqlSharedClient, graphqlSsrClient } from '../../lib/graphql/graphqlSsrClient'
import '../../builder-registry'

builder.init(process.env.NEXT_PUBLIC_BUILDER_API_KEY!)
type Props = {
  page: BuilderContent | null
} & BlogListQuery
type RouteProps = { url: string }
type GetPageStaticProps = GetStaticProps<LayoutNavigationProps, Props, RouteProps>

export default function BlogPage({
  page,
  blogPosts,
}: {
  page: BuilderContent | null
  blogPosts: BlogListQuery['blogPosts']
}) {
  const router = useRouter()
  const isPreviewing = useIsPreviewing()

  // If the page content is not available
  // and not in preview mode, show a 404 error page
  if (!page && !isPreviewing) {
    return <DefaultErrorPage statusCode={404} />
  }

  // If the page content is available, render
  // the BuilderComponent with the page content
  return (
    <>
      <PageMeta
        title={page?.data?.metaTitle?.Default || page?.data?.title}
        metaDescription={page?.data?.metaDescription?.Default}
        metaRobots={['index', 'follow']}
      />
      <BuilderComponent model='author-bio' content={page || undefined} />
      <BlogList blogPosts={blogPosts} />
    </>
  )
}

BlogPage.pageOptions = {
  Layout: LayoutNavigation,
} as PageOptions

export async function getStaticPaths({ locales = [] }) {
  // Get a list of all pages in Builder
  const pages = await builder.getAll('author-bio', {
    // We only need the URL field
    fields: 'data.url',
    options: { noTargeting: true },
  })

  // Hygraph post paths

  const responses = locales.map(async (locale) => {
    const staticClient = graphqlSsrClient(locale)
    const BlogPostPaths = staticClient.query({ query: BlogPostPathsDocument })
    const { pages } = (await BlogPostPaths).data
    return (
      pages.map((page) => ({ params: { url: `${page?.url}`.replace('blog/', '') }, locale })) ?? []
    )
  })
  const paths = (await Promise.all(responses)).flat(1)

  // Generate the static paths for all pages in Builder

  return {
    paths: [
      ...paths,
      ...(pages
        .map((page) => page.data?.url)
        .filter((url) => url !== '/')
        .map((url) => ({ params: { url: `${url}`.replace('/author/', '') }, locale: 'en' })) ?? []),
    ],
    fallback: 'blocking',
  }
}
export const getStaticProps: GetPageStaticProps = async ({ locale, params }) => {
  const urlKey = params?.url ?? '??'
  const path = `/author/${urlKey}`

  const client = graphqlSharedClient(locale)
  const staticClient = graphqlSsrClient(locale)
  const conf = client.query({ query: StoreConfigDocument })
  const builderPost = await builder
    .get('author-bio', {
      userAttributes: {
        urlPath: path,
      },
      limit: 1,
      includeRefs: true,
      includeUnpublished: false,
    })
    .toPromise()

  const builderBlogPosts = builder.getAll('blog-article', {
    // We only need the URL field
    options: { noTargeting: true },
  })
  const layout = staticClient.query({ query: LayoutDocument, fetchPolicy: 'cache-first' })

  const hygraphBlogPosts = staticClient.query({
    query: BlogListDocument,
    variables: { currentUrl: [`blog/${urlKey}`], first: 4 },
  })

  const blogPosts = [
    ...(await builderBlogPosts).map((post) => ({
      title: post?.data?.title,
      url: post?.data?.url.startsWith('/') ? post?.data?.url.substr(1) : post?.data?.url,
      date: post?.data?.date
        ? new Date(post?.data?.date as number).toISOString()
        : new Date().toISOString(),
      asset:
        post?.data?.heroImage || post?.data?.thumbnail
          ? {
              url: post?.data?.heroImage || post?.data?.thumbnail || '',
              width: 900,
              height: 600,
            }
          : null,
    })),
    ...(await hygraphBlogPosts).data.blogPosts,
  ]

  // Return the page content as props
  return {
    props: {
      page: builderPost || null,
      blogPosts,
      ...(await layout).data,
      apolloState: await conf.then(() => client.cache.extract()),
    },
    revalidate: process.env.SKIP_ISG ? 1 : 60 * 5,
  }
}
